import React, { useState, useEffect } from 'react';
import { Button, message, Space, Spin, Tabs } from 'antd';
import axios from 'axios';
import { format } from 'date-fns';
import GenericTable from '../components/GenericTable';
import GenericModal from '../components/GenericModal ';
import './ApiKeys.css';
import AppMenu from '../components/AppMenu';

const { TabPane } = Tabs;

const ApiKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [providers, setProviders] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentApiKey, setCurrentApiKey] = useState(null);
  const [initialFormData, setInitialFormData] = useState({});
  const [activeKey, setActiveKey] = useState('default');
 
  useEffect(() => {
    fetchApiKeys();
    fetchProviders();
    fetchOrganizations();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/apikeys`);

      const formattedApiKeys = response.data.map(apiKey => {
        const providerName = apiKey.provider?.name || 'N/A';
        return {
          id: apiKey.id,
          name: apiKey.name,
          key: apiKey.key,
          subKey: apiKey.subKey || 'N/A',
          region: apiKey.region || 'N/A',
          providerName: providerName,
          isDefault: apiKey.isDefault ? 'True' : 'False',
          isActive: apiKey.isActive ? 'ACTIVE' : 'INACTIVE',
          createdAt: format(new Date(apiKey.createdAt), 'MM/dd/yyyy, HH:mm:ss'),
          updatedAt: format(new Date(apiKey.updatedAt), 'MM/dd/yyyy, HH:mm:ss'),
        };
      });

      setApiKeys(formattedApiKeys);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      message.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/providers`);
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
      message.error('Failed to load providers');
    } finally {
      setProvidersLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/organizations`);
      setOrgs(response.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      message.error('Failed to load organizations');
    }
  };

  const openModal = (apiKey = null, orgId = null) => {
    setCurrentApiKey(apiKey);
    setInitialFormData(apiKey || {});
    setIsModalVisible(true);

    if (orgId || activeKey.startsWith('org-')) {
      const selectedOrgId = orgId || activeKey.split('-')[1];
      setInitialFormData(prev => ({ ...prev, organizationId: selectedOrgId }));
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setCurrentApiKey(null);
    setInitialFormData({});
  };

  const handleCreateUpdateApiKey = async formData => {
    try {
      const endpoint = activeKey === 'default' ? 'apikeys' : 'orgApiKey';
      const url = `${process.env.REACT_APP_API}/api/${endpoint}`;

      let dataToSend = {};

      if (activeKey === 'default') {
        dataToSend = {
          name: formData.name,
          key: formData.key,
          subKey: formData.subKey || null,
          region: formData.region || null,
          providerId: formData.providerId || null,
          isActive:  true,
          isDefault: formData.isDefault === 'True' || formData.isDefault === true,
        };
      } else {
        dataToSend = {
          name: formData.name,
          ApikeyId: formData.ApikeyId,
          organizationId: formData.organizationId || activeKey.split('-')[1],
        };
      }

      if (currentApiKey) {
        await axios.patch(`${url}/${currentApiKey.id}`, dataToSend);
        message.success(
          `${endpoint === 'apikeys' ? 'API key' : 'Org API key'} updated successfully`
        );
      } else {
        await axios.post(url, dataToSend);
        message.success(
          `${endpoint === 'apikeys' ? 'API key' : 'Org API key'} created successfully`
        );
      }

      if (activeKey === 'default') {
        fetchApiKeys();
      }
      closeModal();
    } catch (error) {
      console.error('Error creating/updating API key:', error);
      message.error(error.response.data.message);
    }
  };

  const handleDeleteApiKey = async apiKey => {
    try {
      await axios.patch(`${process.env.REACT_APP_API}/api/apikeys/${apiKey.id}/org-activate`, {});
      message.success('API key Status Updated successfully');
      fetchApiKeys();
    } catch (error) {
      console.error('Error Change Status API key:', error);
      message.error('Failed to Change status in API key');
    }
  };

  const handleSetDefaultApiKey = async apiKeyId => {
    try {
      await axios.patch(`${process.env.REACT_APP_API}/api/apikeys/${apiKeyId}/set-default`, {});
      message.success('API key set as default successfully.');
      fetchApiKeys(); // Refresh the list after setting the default
    } catch (error) {
      console.error('Error setting API key as default:', error);
      message.error('Failed to set the API key as default.');
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
  }

  return (
    <div style={{ display: 'flex' }}>
      <AppMenu />
      <div className="apikeys-page">
        <Space style={{ width: '100%', display: 'flex', justifyContent: 'end', marginBottom: 20, marginTop: '20px' }}>
          <Button type="primary" onClick={() => openModal()}>
            Create API Key
          </Button>
        </Space>
        <GenericTable
          data={apiKeys}
          searchKeys={['name', 'providerName', 'key']}
          sortableColumns={['name', 'providerName', 'createdAt', 'updatedAt']}
          actions={['Toggle Status', 'Set Default']}
          actionFunctions={[
            apiKey => handleDeleteApiKey(apiKey),
            apiKey => handleSetDefaultApiKey(apiKey.id), // Set Default API key
          ]}
          toggle="isActive"
          isDefault="isDefault"
        />

        {/* Modal for Create/Update API Key */}
        <GenericModal
          isVisible={isModalVisible}
          handleOk={handleCreateUpdateApiKey}
          handleCancel={closeModal}
          title={currentApiKey ? 'Edit API Key' : 'Create API Key'}
          inputs={[
            ...(activeKey === 'default'
              ? [
                  {
                    label: 'Name',
                    name: 'name',
                    type: 'text',
                    placeholder: 'Enter API key name',
                    require: true,
                  },
                  {
                    label: 'Key',
                    name: 'key',
                    type: 'text',
                    placeholder: 'Enter API key',
                    require: true,
                  },
                  {
                    label: 'Sub Key',
                    name: 'subKey',
                    type: 'text',
                    placeholder: 'Enter sub-key (optional)',
                  },
                  {
                    label: 'Region',
                    name: 'region',
                    type: 'text',
                    placeholder: 'Enter region (optional)',
                  },
                  {
                    label: 'Is Default',
                    name: 'isDefault',
                    type: 'select',
                    require: true,
                    options: [
                      { value: 'True', label: 'True' },
                      { value: 'False', label: 'False' },
                    ],
                    placeholder: 'Select default status',
                  },
                  {
                    label: 'Provider',
                    name: 'providerId',
                    type: 'select',
                    require: true,
                    options: providers.map(provider => ({
                      value: provider.id,
                      label: provider.name,
                    })),
                    placeholder: 'Select provider',
                    loading: providersLoading,
                  },
                ]
              : [
                  {
                    label: 'Name',
                    name: 'name',
                    type: 'text',
                    placeholder: 'Enter Org API key name',
                    require: true,
                  },
                  {
                    label: 'API Key',
                    name: 'ApikeyId',
                    type: 'select',
                    options: apiKeys.map(key => ({
                      value: key.id,
                      label: key.name,
                    })),
                    placeholder: 'Select an existing API Key',
                    require: true,
                  },
                  {
                    label: 'Organization',
                    name: 'organizationId',
                    type: 'select',
                    options: orgs.map(org => ({
                      value: org.id,
                      label: org.name,
                    })),
                    placeholder: 'Select Organization',
                    hidden: activeKey.startsWith('org-'),
                    disabled: activeKey.startsWith('org-'),
                    require: true,
                  },
                ]),
          ]}
          actions={['Submit', 'Cancel']}
          actionFunctions={[handleCreateUpdateApiKey, closeModal]}
          predefinedData={initialFormData}
        />
      </div>
    </div>
  );
};

export default ApiKeys;
